/* eslint-disable @typescript-eslint/naming-convention */
import type { UiState } from 'instantsearch.js';
import qs from 'qs';

import {
	INSTANT_SEARCH_SORT_BY_LEAST_SIGNATURES,
	INSTANT_SEARCH_SORT_BY_MOST_SIGNATURES,
	INSTANT_SEARCH_SORT_BY_NEWEST,
} from '#src/app/pages/search/shared/consts';
import type { CustomRouteSearchState, SortByEnum } from '#src/app/pages/search/shared/types';
import { SEARCH_QUERY_KEYS } from '#src/app/pages/search/shared/types';

const getSortByFromStateToRoute = ({
	sortBy,
	instantSearchIndexName,
}: {
	sortBy: string | undefined;
	instantSearchIndexName: string;
}): SortByEnum | undefined => {
	if (sortBy === `${instantSearchIndexName}${INSTANT_SEARCH_SORT_BY_MOST_SIGNATURES}`) {
		return 'most_signatures';
	}

	if (sortBy === `${instantSearchIndexName}${INSTANT_SEARCH_SORT_BY_LEAST_SIGNATURES}`) {
		return 'least_signatures';
	}

	if (sortBy === `${instantSearchIndexName}${INSTANT_SEARCH_SORT_BY_NEWEST}`) {
		return 'newest';
	}

	return undefined;
};

const getSortByFromRouteToState = ({
	sortBy,
	instantSearchIndexName,
}: {
	sortBy: SortByEnum | undefined;
	instantSearchIndexName: string;
}): string | undefined => {
	if (sortBy === 'most_signatures') {
		return `${instantSearchIndexName}${INSTANT_SEARCH_SORT_BY_MOST_SIGNATURES}`;
	}

	if (sortBy === 'least_signatures') {
		return `${instantSearchIndexName}${INSTANT_SEARCH_SORT_BY_LEAST_SIGNATURES}`;
	}

	if (sortBy === 'newest') {
		return `${instantSearchIndexName}${INSTANT_SEARCH_SORT_BY_NEWEST}`;
	}
	return undefined;
};

const getRefinementListRouteToState = (routeState: CustomRouteSearchState): Record<string, string[]> | undefined => {
	const { topics, location_city, location_country_code } = routeState;

	const refinementList: Record<string, string[]> = {};
	if (topics) {
		refinementList.tags = topics.split('+').map(decodeURIComponent);
	}
	if (location_city) {
		refinementList['location.city_state'] = location_city.split('+').map(decodeURIComponent);
	}
	if (location_country_code) {
		refinementList['location.country_code'] = location_country_code.split('+').map(decodeURIComponent);
	}
	return Object.keys(refinementList).length > 0 ? refinementList : undefined;
};

// https://www.algolia.com/doc/guides/building-search-ui/going-further/routing-urls/js/

/**
 * Converts the UI state to a custom route search state.
 * This function maps the internal UI state to a custom state that can be used in the URL.
 *
 * @param uiState - The current UI state of the search.
 * @returns The custom route search state.
 */
// eslint-disable-next-line complexity
export const stateToRoute = (uiState: UiState, instantSearchIndexName: string): CustomRouteSearchState => {
	const indexUiState = uiState[instantSearchIndexName];
	const { query, page, toggle, refinementList, sortBy, range } = indexUiState;

	const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
	const onLandingPage = !Object.keys(queryParams).some((key) => SEARCH_QUERY_KEYS.includes(key));

	return {
		// if we're on the search results page, let's always make sure that one of the search params is set
		// so that a search query clear doesn't bring the user back to the landing page
		q: !onLandingPage ? query || '' : query,
		page,
		victory: toggle?.status ? true : undefined,
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		topics: refinementList?.tags?.map(encodeURIComponent).join('+') || undefined,
		sort: getSortByFromStateToRoute({ sortBy, instantSearchIndexName }),
		location_city: refinementList?.['location.city_state']
			? refinementList['location.city_state'].map(encodeURIComponent).join('+')
			: undefined,
		location_country_code: refinementList?.['location.country_code']
			? refinementList['location.country_code'].map(encodeURIComponent).join('+')
			: undefined,
		weekly_signature_count: range?.weekly_signature_count
			? encodeURIComponent(range.weekly_signature_count)
			: undefined,
	};
};

/**
 * Converts the custom route search state back to the UI state.
 * This function maps the custom state from the URL back to the internal UI state.
 *
 * @param routeState - The custom route search state from the URL.
 * @returns The UI state of the search.
 */
export const routeToState = (routeState: CustomRouteSearchState, instantSearchIndexName: string): UiState => {
	const { q, page, victory, sort } = routeState;

	return {
		[instantSearchIndexName]: {
			query: q,
			page: page || undefined,
			toggle: victory
				? {
						status: victory,
					}
				: undefined,
			refinementList: getRefinementListRouteToState(routeState),
			sortBy: getSortByFromRouteToState({ sortBy: sort, instantSearchIndexName }),
			range: routeState.weekly_signature_count
				? {
						weekly_signature_count: decodeURIComponent(routeState.weekly_signature_count),
					}
				: undefined,
		},
	};
};
